import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { UserRole } from '@vayu-saathi/shared-types';
import { queryClient } from '@/lib/query-client';

export interface AuthUser {
  id: string;
  organizationId: string;
  orgName: string;
  role: UserRole;
  firstName: string;
  lastName: string;
  workEmail: string | null;
  employeeCode: string;
  orgLogoUrl?: string | null;
  avatarUrl?: string | null;
  mustChangePassword?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: AuthUser) => void;
  logout: () => void;
}

// FE-02 FIX: Move token storage from localStorage to sessionStorage.
//
// What was wrong:
//   localStorage persists tokens FOREVER across browser restarts.  Any
//   malicious script injected via XSS (or a compromised browser extension)
//   can read localStorage at any time, including days later.
//
// What we do instead:
//   • accessToken / refreshToken / user / isAuthenticated → sessionStorage
//     (tab-scoped; cleared when the tab or browser closes, and on logout)
//
// Why the refresh token is persisted too:
//   It used to live in memory only, so any reload (F5, a deploy, Chrome discarding
//   the tab) dropped it and the session ended at the next 401 — within 15 minutes,
//   mid-form, with the input lost. sessionStorage already meets the goal that a
//   session must not outlive the tab, so both tokens now share it.
//
//   The ideal long-term fix is to have the API set the refresh token in an
//   httpOnly, Secure, SameSite=Strict cookie so it never touches JavaScript
//   at all.  That requires a backend change and is tracked separately.
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      setTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken, isAuthenticated: true });
      },

      setUser: (user) => {
        set({ user });
      },

      logout: () => {
        // Wipe all UI session state (tab positions, active filters, etc.)
        // so a different user logging in on the same browser tab starts clean.
        try {
          Object.keys(sessionStorage)
            .filter((k) => k.startsWith('vayu_saathi_ui_'))
            .forEach((k) => {
              sessionStorage.removeItem(k);
            });
        } catch {
          // sessionStorage unavailable — ignore
        }

        // Drop every cached query and mutation (profile, payslips, bank details …) so the
        // next person to sign in on this tab never sees or re-saves the previous user's data.
        // Removing the queries also cancels any fetch still in flight.
        queryClient.clear();

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: 'vayu-saathi-auth',
      // Changed from localStorage → sessionStorage
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
